import { useTranslation } from 'react-i18next'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaArrowDownRegular, FaArrowUpRegular } from '@enechain/ecloud-icons'

import { ChartTooltip } from '~/components/common/ChartTooltip'
import { AREA_COLORS } from '~/constants/supplyCapacityChange'
import type { ChartModel } from '~/pages/SupplyCapacityChange/models/supplyCapacity'
import { fmtMw } from '~/pages/SupplyCapacityChange/models/supplyCapacity'

type Props = {
  chart: ChartModel
}

const gridColor = LightModeSemanticColors.Border.lowEmphasis.value
const tickColor = LightModeSemanticColors.Text.midEmphasis.value
const zeroLineColor = LightModeSemanticColors.Text.lowEmphasis.value
const accentColor = LightModeSemanticColors.Surface.accentPrimary.value
const tealColor = LightModeSemanticColors.Brand.teal.value
const lowAreaColor = LightModeSemanticColors.Text.lowEmphasis.value

export const ImpactChart = ({ chart }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { data, activeAreas, todayNet, tmrNet } = chart

  return (
    <Flex flexDirection="column" flex={1} gap={2} minWidth={0}>
      {/* 因果ガイド: 停止開始→供給減→価格上昇 / 停止終了→供給回復→価格下落 */}
      <Flex
        textStyle="captionStrong"
        justifyContent="space-between"
        paddingX={1}
      >
        <Text
          as="span"
          alignItems="center"
          gap={1}
          display="inline-flex"
          color="Text.negativeHigh"
        >
          <FaArrowDownRegular boxSize="12px" />
          {t('page:supplyCapacityChange.impact.guideDown')}
        </Text>
        <Text
          as="span"
          alignItems="center"
          gap={1}
          display="inline-flex"
          color="Text.positiveMid"
        >
          <FaArrowUpRegular boxSize="12px" />
          {t('page:supplyCapacityChange.impact.guideUp')}
        </Text>
      </Flex>
      <Box
        height="420px"
        minHeight="360px"
        padding={1}
        border={`1px solid ${gridColor}`}
        borderRadius="lg"
        backgroundColor="Surface.primary"
      >
        <ResponsiveContainer width="100%" height="100%">
          {/* recharts (3.8.1) は **mount 後に増えた系列を登録順 = 末尾**に積む。
              `activeAreas` は `areaOrder` を走査して純変化が 0 でないエリアだけを
              push するので、データ更新で途中のエリアが有効化されると
              そのバーが `areaOrder` の位置ではなく**一番上**に積まれてしまう。
              系列の並びを `key` にして、構成が変わった時だけ作り直す
              (同じ対処を BalanceGeneration の BgChart にも入れている)。 */}
          <BarChart
            key={activeAreas.join('-')}
            margin={{ top: 28, right: 16, left: 0, bottom: 4 }}
            data={data}
          >
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis
              dataKey="category"
              tick={{ fontSize: 12, fill: tickColor }}
            />
            <YAxis
              width={52}
              label={{
                value: 'MW',
                angle: -90,
                position: 'insideLeft',
                offset: 10,
                style: { fontSize: 12, fill: tickColor },
              }}
              tick={{ fontSize: 11, fill: tickColor }}
            />
            {/* 8080 共通 tooltip (design `js/charts.js` の `TIP`) を適用する
                (2026-09-01 デザイナー指摘)。 recharts 既定の tooltip は器も
                typography も DS 外で、 8080 の 20 画面が共有する見た目と揃わない。
                値は符号つき純変化なので、 カード・基準線と同じ `fmtMw` で出す
                (既定の `Math.round().toLocaleString()` だと -600 が「600」 になり
                増減が読めなくなる)。 */}
            <Tooltip content={<ChartTooltip formatValue={fmtMw} />} />
            <Legend
              iconType="square"
              wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
            />
            <ReferenceLine stroke={zeroLineColor} strokeWidth={2} y={0} />
            <ReferenceLine
              stroke={accentColor}
              label={{
                value: `${t('page:supplyCapacityChange.impact.today')} ${fmtMw(todayNet)} MW`,
                position: 'insideTopLeft',
                fill: accentColor,
                fontSize: 11,
                fontWeight: 700,
              }}
              strokeDasharray="5 4"
              strokeWidth={2}
              y={todayNet}
            />
            <ReferenceLine
              stroke={tealColor}
              label={{
                value: `${t('page:supplyCapacityChange.impact.tomorrow')} ${fmtMw(tmrNet)} MW`,
                position: 'insideTopRight',
                fill: tealColor,
                fontSize: 11,
                fontWeight: 700,
              }}
              strokeDasharray="5 4"
              strokeWidth={2}
              y={tmrNet}
            />
            {activeAreas.map((area) => (
              <Bar
                key={area}
                fill={AREA_COLORS[area] ?? lowAreaColor}
                dataKey={area}
                isAnimationActive={false}
                name={area}
                stackId="net"
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Flex>
  )
}
